/**
 * Shared pure-math scene-geometry builder for the REVCLOUD entity (DEFUSE
 * Direction-3 — clone elimination).
 *
 * Single one-truth for the revision-cloud tessellation, called by BOTH the
 * legacy `../revisionCloudToRenderable.ts` (reads the `EntityPreview.properties`
 * string bag) and the typed `../typed/revisionCloudFromEntity.ts` (reads the
 * canonical `RevisionCloudEntity` fields). The cloud-bulge sampling math was
 * byte-identical between the two paths AFTER value extraction — only the ~3-line
 * value source differs — so the math now lives here ONCE and each adapter is
 * reduced to its value extraction + a single builder call. dup:check therefore
 * sees NO typed<->legacy clone.
 *
 * # AutoCAD model
 *
 * AutoCAD's REVCLOUD is a closed polyline whose every segment is replaced by a
 * half-circle arc bulge (the cloud-bubble look). The drawing-model carries the
 * underlying polyline vertices plus an `approximateArcLength` value driving
 * bulge curvature. Our 3D renderer feeds the resulting arc chain through the
 * line pipeline (existing `batchedLayer` path), so this builder emits a
 * `kind: "polyline"` `SceneGeometry` whose vertices already trace the cloud
 * curve.
 *
 * # Inputs (already-parsed primitives)
 *
 *   - `verticesRaw`  raw (PRE-offset) polyline vertices. `applyOffset` is
 *                    applied INSIDE the builder per vertex (exactly as the
 *                    legacy `readVertices` did), so callers pass world-space
 *                    x/y/z untouched. z defaults to 0 at the call site for both
 *                    paths (the legacy bag drops z; the typed model vertex has
 *                    no z) — see the adapters.
 *   - `closed`       whether the closing edge gets a bulge.
 *   - `arcLength`    approximate arc length (NOT yet clamped — the builder
 *                    applies the same `Math.max(1, …)` floor both paths used).
 *
 * # Return
 *
 *   - `{ geometry, bounds }` when ≥ 2 effective vertices produced ≥ 2 sampled
 *     points; otherwise `null` (matches the legacy `return null` short-circuits
 *     so both adapters can early-return the SAME way).
 *
 * Pure: no `EntityPreview` / `DrawingEntity` import, no side effects beyond the
 * locked-in coordinate origin via `applyOffset`.
 */

import { clampRevcloudChord } from "@dxf-ai-editor/command-engine";

import { applyOffset } from "../../coordinateOrigin";
import type { SceneGeometry, Vec3 } from "../../types";
import { boundsFromPoints } from "../adapterContext";

const ARC_SAMPLE_COUNT = 16;

/** AABB shape carried on `RenderableEntity.bounds` (matches `boundsFromPoints`). */
interface SceneBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** `{ geometry, bounds }` pair the builder returns (`null` when degenerate). */
export interface RevisionCloudGeometryResult {
  geometry: SceneGeometry;
  bounds: SceneBounds;
}

/**
 * Build the revision-cloud `polyline` geometry + AABB from the raw (pre-offset)
 * vertices, the `closed` flag, and the (un-clamped) approximate arc length.
 *
 * `applyOffset` is applied to every vertex INSIDE the builder (identical to the
 * legacy `readVertices`). The arc length is floored at 1 here (the same
 * `Math.max(1, …)` both adapters applied) so neither caller has to.
 *
 * Returns `null` when fewer than 2 offset vertices are supplied OR the
 * tessellation produced fewer than 2 sampled points — matching the two legacy
 * `return null` guards so both adapters early-return identically.
 */
export function buildRevisionCloudSceneGeometry(
  verticesRaw: readonly Vec3[],
  closed: boolean,
  arcLength: number,
): RevisionCloudGeometryResult | null {
  const baseVertices = verticesRaw.map((v) => applyOffset(v));
  if (baseVertices.length < 2) return null;

  // Floor the approximate arc length via the SHARED command-engine clamp
  // (`MIN_REVCLOUD_ARC_CHORD` = 1) — the same floor the Object-mode outline
  // tracer applies, so traced chains and rendered bulges stay in sync.
  const effectiveArcLength = clampRevcloudChord(arcLength);

  const points: Vec3[] = [];
  const segCount = baseVertices.length;
  // Count segments accounting for the closing segment when closed.
  const lastIndex = closed ? segCount : segCount - 1;
  for (let i = 0; i < lastIndex; i++) {
    const a = baseVertices[i];
    const b = baseVertices[(i + 1) % segCount];
    if (!a || !b) continue;
    sampleCloudBulge(a, b, effectiveArcLength, points, i === 0);
  }

  // sampleCloudBulge appends arc samples; if `closed` we add the very last
  // vertex as a final cap. Otherwise the routine already does.
  if (closed && points.length > 0) {
    const first = points[0];
    if (first) points.push(first);
  }

  if (points.length < 2) return null;

  const geometry: SceneGeometry = {
    kind: "polyline",
    points,
    closed: false,
  };
  const bounds = boundsFromPoints(points);

  return { geometry, bounds };
}

/**
 * Sample one cloud-bulge segment between two anchor vertices. The bulge sits to
 * the LEFT of the directed edge (matches AutoCAD's convention for the default
 * clockwise REVCLOUD; CCW REVCLOUD reverses the vertex order at construction
 * time so the same routine works for both).
 */
function sampleCloudBulge(
  a: Vec3,
  b: Vec3,
  arcLength: number,
  out: Vec3[],
  emitStart: boolean,
): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-9) return;
  // Number of bulges along this edge — ≥ 1.
  const bulgeCount = Math.max(1, Math.round(length / arcLength));
  const stepLen = length / bulgeCount;
  // Each bulge spans `stepLen` along the edge with a half-circle bulge height of
  // `stepLen / 2` (acad-cloud default sagitta).
  const ux = dx / length;
  const uy = dy / length;
  // Perpendicular pointing left of the edge direction (AutoCAD bulges appear on
  // the inside of the cloud — clockwise REVCLOUD => perpendicular points
  // outward; reusing left side here keeps rectangular-cloud unit tests stable).
  const nx = -uy;
  const ny = ux;

  if (emitStart) out.push({ x: a.x, y: a.y, z: a.z });
  for (let bi = 0; bi < bulgeCount; bi++) {
    const baseT = bi / bulgeCount;
    // For each bulge sample ARC_SAMPLE_COUNT mid-points + endpoint.
    for (let s = 1; s <= ARC_SAMPLE_COUNT; s++) {
      const t = baseT + s / (bulgeCount * ARC_SAMPLE_COUNT);
      const mid = t - (baseT + 1 / (2 * bulgeCount));
      // Offset along edge (px) + perpendicular bulge (sinusoidal).
      const along = a.x + ux * length * t;
      const alongY = a.y + uy * length * t;
      // Sin-bulge — peaks at t=baseT+1/(2*bulgeCount), height = stepLen/2.
      const phase = ((s / ARC_SAMPLE_COUNT) - 0.5) * Math.PI;
      const bulgeHeight = (stepLen / 2) * Math.cos(phase);
      out.push({
        x: along + nx * bulgeHeight,
        y: alongY + ny * bulgeHeight,
        z: a.z,
      });
      // mid suppresses unused warning when target=es2022 with
      // verbatimModuleSyntax — explicit acknowledgement of the computed phase.
      void mid;
    }
  }
}
